import { UNWANTED_FULLSTORY_TRACK_CALL_PROPERTIES } from '../constants';
import { MetadataUtils } from '../types/metadata_utils';

const DOMAIN_WHITELIST = ['gusto.', 'zenpayroll.', 'gusto-dev.', 'gusto-staging.', 'gusto-demo.'];

const ZP_SUBDOMAIN_WHITELIST = ['manage.', 'app.'];

const DCLID = 'dclid';
const FBCLID = 'fbclid';
const GCLID = 'gclid';
const GTM_PREFIX = 'gtm_';
const GUSTO_TRACKING_CODE = 'idgm';
const INVITE_TOKEN = 'invite_token';
const IRCLICKID = 'irclickid';
const MSCLKID = 'msclkid';
const REFERRAL_TOKEN = 'referral_token';
const UTM_PREFIX = 'utm_';
const VT_PREFIX = 'vt_';
const PREFIXES = [GTM_PREFIX, UTM_PREFIX, VT_PREFIX];
const IDS = [
  DCLID,
  FBCLID,
  GCLID,
  GUSTO_TRACKING_CODE,
  INVITE_TOKEN,
  IRCLICKID,
  MSCLKID,
  REFERRAL_TOKEN,
];

export class PageHelper {
  public readonly INVITE_FRIENDS = {
    NAME: 'gcorp invite friends',
    PATH: '/invite/friends/',
  };
  public readonly EXCLUDED_UUID_EVENT_CATEGORIES = ['CommandBar'];

  // These are not considered gcorp pages even though they are nested under gcorp
  private readonly GCORP_OTHER_PROP_PATH = [
    '/framework/',
    '/company-news/',
    '/ask-gusto/',
    '/partner-resources/',
    this.INVITE_FRIENDS.PATH,
  ];
  private readonly HOSTNAME_START_TO_PREFIX_MAP = {
    'credits.gusto.': 'credits',
    'engineering.gusto.': 'engineering',
    'framework.gusto-dev.': 'framework',
    'framework.gusto.': 'framework',
    'get.gusto.': 'webflow',
    'go.gusto.': 'marketo',
    'support.gusto.': 'support',
  };
  private readonly CATEGORIZED_PAGES = [
    'framework',
    'company-news',
    'ask-gusto',
    'partner-resources',
    'support',
    'marketo',
    'zp',
    'engineering',
  ];

  constructor(private readonly metadataUtils: MetadataUtils) {}

  public isZpPage(hostname: string, port: string): boolean {
    const subdomainAndDomainMatch = ZP_SUBDOMAIN_WHITELIST.some(subdomain =>
      DOMAIN_WHITELIST.some(domain => hostname.startsWith(`${subdomain}${domain}`)),
    );
    const isLocalHost = hostname === 'localhost' && port === '3000';
    return subdomainAndDomainMatch || isLocalHost;
  }

  public constructPageName() {
    const path = this.metadataUtils.getWindowLocationPathname();
    const prefix = this.getPrefix(path);
    const pageTitle = this.metadataUtils.getDocumentTitle();
    const defaultName = pageTitle ? pageTitle : path;

    // Update 20250328 now retuning the full url if it's not a gcorp page
    // previously it would return '' which makes page load not fire.
    return !prefix && !this.isOtherGcorpPage(path) ? defaultName : nameFromPath(path, prefix);
  }

  // Return true if pageName, constructed using constructPageName(), is a page from framework/helper center/blog
  // Return false otherwise
  // Return null if pageName is not a string
  public isCategorizedPage(pageName?: string) {
    if (typeof pageName !== 'string') {
      return null;
    }
    const pageCategory = this.getPageCategory(pageName);
    if (!pageCategory) {
      return false;
    }

    return this.CATEGORIZED_PAGES.indexOf(pageCategory) !== -1;
  }

  // Return first word in pageName constructed using constructPageName()
  // Return null if pageName is not a string
  public getPageCategory(pageName?: string) {
    return typeof pageName === 'string' ? pageName.split(' ')[0] : null;
  }

  // Get the friend name for the path
  public getFriendName() {
    const friendName = this.metadataUtils
      .getWindowLocationPathname()
      .split(this.INVITE_FRIENDS.PATH);

    return friendName.length > 1 && friendName[1];
  }

  // Check if framework/company-news/ask-gusto
  // Only check this if hostname is valid gcorp hostname
  // Note that framework/company-news/ask-gusto/partner-resources are NOT considered Gcorp pages
  public isOtherGcorpPage(path: string): boolean {
    return this.GCORP_OTHER_PROP_PATH.some(propPath => path.startsWith(propPath));
  }

  public isGcorpPage(hostname: string, path: string, port: string): boolean {
    const isLocalDevEnv = hostname === 'localhost' && port === '3001';
    return (
      (DOMAIN_WHITELIST.find(d => hostname.startsWith(d)) !== undefined || isLocalDevEnv) &&
      !this.isOtherGcorpPage(path)
    );
  }

  // Return pageName minus the category (pageName constructed using constructPageName())
  // Return null if pageName is not a string
  // Return 'home' if subName is empty
  public getPageSubName(pageName?: string) {
    if (typeof pageName !== 'string') {
      return null;
    }
    const subName = pageName.split(' ').slice(1).join(' ');
    return subName || 'home';
  }

  // Get referrer, path, url, and title and return them as an object
  public getPageInfo() {
    const data: any = {};
    const referrer = this.metadataUtils.getDocumentReferrer();
    const path = this.metadataUtils.getWindowLocationPathname();
    const url = this.metadataUtils.getWindowLocationHref();
    const title = this.metadataUtils.getDocumentTitle();

    if (referrer) {
      data.referrer = referrer;
    }

    if (path) {
      data.path = path;
    }

    if (url) {
      data.url = url;
    }

    if (title) {
      data.title = title;
    }

    return data;
  }

  // Extract all tracking params and values (utm_*, vt_*, gclid) and return an object
  public getTrackingParams(url = this.metadataUtils.getWindowLocationHref()) {
    const retObj: Record<string, any> = {};

    if (url) {
      const queryString = url.split('?');

      if (queryString[1]) {
        const params = queryString[1].split('&');
        params
          .map(param => param.split('='))
          .forEach(([k, val]) => {
            const key = k.toLowerCase();
            if (this.startsWithPrefix(key) || this.isPaidId(key)) {
              retObj[key] = val;
            }
          });
      }
    }

    return retObj;
  }

  private prefixFromHostname(hostname: string) {
    const match = Object.entries(this.HOSTNAME_START_TO_PREFIX_MAP).find(([start]) =>
      hostname.startsWith(start),
    );
    return match && match[1];
  }

  private getPrefix(path: string) {
    const hostname = this.metadataUtils.getWindowLocationHostname();
    const port = this.metadataUtils.getWindowLocationPort();

    return (
      this.prefixFromHostname(hostname) ||
      (this.isZpPage(hostname, port) && 'zp') ||
      (this.isGcorpPage(hostname, path, port) && 'gcorp') ||
      ''
    );
  }

  private startsWithPrefix(paramName: string) {
    return PREFIXES.findIndex(prefix => paramName.startsWith(prefix)) !== -1;
  }

  private isPaidId(paramName: string) {
    return IDS.includes(paramName);
  }
}

function nameFromPath(path: string, prefix?: string) {
  const pathLevels = path.split('/').filter(Boolean).join(' ');
  const name = pathLevels.split('.')[0] || 'home';
  return [prefix, name].filter(Boolean).join(' ');
}

export function deepCopy(obj: any): any {
  if (obj == null || obj instanceof Date || typeof obj !== 'object') {
    return obj;
  }
  if (obj instanceof Array) {
    return obj.map(i => deepCopy(i));
  }
  if (obj instanceof Object) {
    return Object.entries(obj).reduce((o, [k, v]) => Object.assign(o, { [k]: deepCopy(v) }), {});
  }
  return JSON.parse(JSON.stringify(obj));
}

export function deepCopyMerge(...objects: any[]) {
  return Object.assign({}, ...objects.map(obj => deepCopy(obj)));
}

export function removeEmpty(objIn: any, doNotCopyNested = false) {
  const obj = doNotCopyNested ? objIn : deepCopy(objIn);
  Object.entries(obj).forEach(([key, value]) => {
    if (value && typeof value === 'object' && Object.entries(value).length) {
      removeEmpty(value, true);
    } else if (value === null || value === undefined) {
      delete obj[key];
    }
  });
  return obj;
}

export function mergeMetaData({ userId, visitorId, sessionId, data }: any) {
  return removeEmpty({ ...data, userId, visitorId, sessionId });
}

function hasPiiKeyword(key: string, piiKeywords: string[], allowList: string[]) {
  const lowerCaseKey = key.toLowerCase();
  return !allowList.includes(lowerCaseKey) && piiKeywords.some(pii => lowerCaseKey.includes(pii));
}

export function removeUnsupportedFullStoryProperties(data: any) {
  const obj = deepCopy(data);
  Object.entries(obj).forEach(([key]) => {
    if (key.startsWith('_')) {
      delete obj[key];
    }
  });
  return obj;
}

export function removePiiEntries(
  objIn: any,
  piiKeywords: string[] = [],
  allowList: string[] = [],
  doNotCopyNested: boolean = false,
) {
  const obj = doNotCopyNested ? objIn : deepCopy(objIn);
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'object' && Object.entries(value ?? {}).length) {
      removePiiEntries(value, piiKeywords, allowList, true);
    } else if (hasPiiKeyword(key, piiKeywords, allowList)) {
      delete obj[key];
    }
  });
  return obj;
}

/**
 * This function filters out data that is not of interest to fullstory due to its limit on attributes
 * @param {*} data The object containing all properties to track
 */
export function removeUnwantedFullStoryProperties(data: any) {
  const obj = deepCopy(data);
  Object.entries(obj).forEach(([key]) => {
    if (UNWANTED_FULLSTORY_TRACK_CALL_PROPERTIES.includes(key) || key.startsWith('gst')) {
      delete obj[key];
    }
  });
  return obj;
}
